import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/apiClient";
import ResetPasswordDialog from "@/components/app/ResetPasswordDialog";
import AddressFields from "@/components/app/AddressFields";
import { toast } from "sonner";
import { Store, Plus, Pencil, KeyRound, MapPin, Hash, X } from "lucide-react";

function RegionsEditor({ cities, setCities, ranges, setRanges }) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm">Cidades atendidas</Label>
          <Button type="button" size="sm" variant="outline" className="h-7 gap-1"
            onClick={() => setCities([...cities, { city: "", uf: "" }])} data-testid="add-city-button">
            <Plus className="h-3 w-3" /> Cidade
          </Button>
        </div>
        {cities.map((c, i) => (
          <div key={i} className="flex gap-2 items-center">
            <Input value={c.city} placeholder="Cidade" className="flex-1"
              onChange={(e) => setCities(cities.map((x, j) => j === i ? { ...x, city: e.target.value } : x))}
              data-testid={`city-input-${i}`} />
            <Input value={c.uf} placeholder="UF" maxLength={2} className="w-16 uppercase"
              onChange={(e) => setCities(cities.map((x, j) => j === i ? { ...x, uf: e.target.value.toUpperCase() } : x))}
              data-testid={`uf-input-${i}`} />
            <button type="button" onClick={() => setCities(cities.filter((_, j) => j !== i))}
              className="text-red-500 p-1" data-testid={`remove-city-${i}`}><X className="h-4 w-4" /></button>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm">Faixas de CEP</Label>
          <Button type="button" size="sm" variant="outline" className="h-7 gap-1"
            onClick={() => setRanges([...ranges, { start: "", end: "" }])} data-testid="add-range-button">
            <Plus className="h-3 w-3" /> Faixa
          </Button>
        </div>
        {ranges.map((r, i) => (
          <div key={i} className="flex gap-2 items-center">
            <Input value={r.start} placeholder="CEP inicial" className="flex-1"
              onChange={(e) => setRanges(ranges.map((x, j) => j === i ? { ...x, start: e.target.value } : x))}
              data-testid={`range-start-${i}`} />
            <span className="text-slate-400 text-sm">até</span>
            <Input value={r.end} placeholder="CEP final" className="flex-1"
              onChange={(e) => setRanges(ranges.map((x, j) => j === i ? { ...x, end: e.target.value } : x))}
              data-testid={`range-end-${i}`} />
            <button type="button" onClick={() => setRanges(ranges.filter((_, j) => j !== i))}
              className="text-red-500 p-1" data-testid={`remove-range-${i}`}><X className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function FranchiseeDialog({ trigger, franchisee, onSaved }) {
  const isEdit = !!franchisee;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", cnpj: "", razao_social: "", base_cep: "" });
  const [cities, setCities] = useState([]);
  const [ranges, setRanges] = useState([]);
  const [addr, setAddr] = useState({});
  const [busy, setBusy] = useState(false);

  const init = () => {
    if (isEdit) {
      setForm({ name: franchisee.name || "", email: franchisee.email || "", phone: franchisee.phone || "", password: "", cnpj: franchisee.cnpj || "", razao_social: franchisee.razao_social || "", base_cep: franchisee.base_cep || "" });
      setCities(franchisee.cities || []);
      setRanges(franchisee.cep_ranges || []);
      setAddr(franchisee.address || {});
    } else {
      setForm({ name: "", email: "", phone: "", password: "", cnpj: "", razao_social: "", base_cep: "" });
      setCities([]);
      setRanges([]);
      setAddr({});
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const cleanCities = cities.filter((c) => c.city && c.uf);
    const cleanRanges = ranges.filter((r) => r.start && r.end);
    try {
      if (isEdit) {
        await api.patch(`/franchisees/${franchisee.id}`, {
          name: form.name, email: form.email, phone: form.phone,
          cnpj: form.cnpj, razao_social: form.razao_social, base_cep: form.base_cep, address: addr,
          cities: cleanCities, cep_ranges: cleanRanges,
        });
        toast.success("Franqueado atualizado!");
      } else {
        await api.post("/franchisees", {
          name: form.name, email: form.email, phone: form.phone, password: form.password,
          cnpj: form.cnpj, razao_social: form.razao_social, base_cep: form.base_cep, address: addr,
          cities: cleanCities, cep_ranges: cleanRanges,
        });
        toast.success("Franqueado criado!");
      }
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) init(); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="franchisee-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar franqueado" : "Novo franqueado"}</DialogTitle>
          <DialogDescription>Defina os dados de acesso e as regiões atendidas.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5"><Label>Nome da franquia</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="franchisee-name-input" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5"><Label>Razão social</Label>
              <Input value={form.razao_social} onChange={(e) => setForm({ ...form, razao_social: e.target.value })} placeholder="Razão social da empresa" data-testid="franchisee-razao-input" /></div>
            <div className="space-y-1.5"><Label>CNPJ</Label>
              <Input value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} placeholder="00.000.000/0000-00" data-testid="franchisee-cnpj-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5"><Label>E-mail</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="franchisee-email-input" /></div>
            <div className="space-y-1.5"><Label>Telefone</Label>
              <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(21) 99999-9999" data-testid="franchisee-phone-input" /></div>
          </div>
          {!isEdit && (
            <div className="space-y-1.5"><Label>Senha de acesso</Label>
              <Input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Mínimo 6 caracteres" data-testid="franchisee-password-input" /></div>
          )}
          <div className="space-y-1.5 rounded-lg border border-slate-200 p-3" data-testid="franchisee-address-section">
            <Label className="font-semibold">Endereço da franquia (aparece na OS)</Label>
            <AddressFields value={addr} onChange={setAddr} idPrefix="franchisee-addr" />
          </div>
          <div className="space-y-1.5">
            <Label>CEP-base da franquia (localização no mapa)</Label>
            <Input value={form.base_cep} onChange={(e) => setForm({ ...form, base_cep: e.target.value })}
              placeholder="00000-000" data-testid="franchisee-base-cep-input" />
            <p className="text-[11px] text-slate-400">Usado para direcionar o chamado ao franqueado mais próximo por GPS. A localização é obtida automaticamente pelo CEP.</p>
          </div>
          <RegionsEditor cities={cities} setCities={setCities} ranges={ranges} setRanges={setRanges} />
          <DialogFooter>
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={busy} data-testid="submit-franchisee-button">
              {busy ? "Salvando..." : isEdit ? "Salvar alterações" : "Criar franqueado"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function FranchiseesTab({ franchisees, reload }) {
  const [resetResult, setResetResult] = useState(null);

  const resetPassword = async (f) => {
    try {
      const { data } = await api.post(`/franchisees/${f.id}/reset-password`);
      setResetResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <FranchiseeDialog onSaved={reload}
          trigger={<Button className="bg-blue-600 hover:bg-blue-700 gap-1.5" data-testid="add-franchisee-button"><Plus className="h-4 w-4" /> Adicionar franqueado</Button>} />
      </div>
      {franchisees.length === 0 ? (
        <p className="text-slate-500 py-10 text-center" data-testid="empty-franchisees">Nenhum franqueado cadastrado.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="franchisees-list">
          {franchisees.map((f) => (
            <Card key={f.id} className="border-slate-200" data-testid={`franchisee-${f.id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center">
                    <Store className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-800 truncate">{f.name}</p>
                    <p className="text-xs text-slate-500 truncate">{f.email} · {f.phone || "-"}</p>
                    {(f.razao_social || f.cnpj) && (
                      <p className="text-xs text-slate-500 truncate" data-testid={`franchisee-doc-${f.id}`}>
                        {f.razao_social || ""}{f.razao_social && f.cnpj ? " · " : ""}{f.cnpj ? `CNPJ ${f.cnpj}` : ""}
                      </p>
                    )}
                  </div>
                </div>
                <div className="space-y-1.5">
                  {(f.cities || []).length > 0 && (
                    <div className="flex items-start gap-1.5 text-xs text-slate-600">
                      <MapPin className="h-3.5 w-3.5 mt-0.5 text-slate-400" />
                      <span className="flex flex-wrap gap-1">
                        {f.cities.map((c, i) => <Badge key={i} variant="secondary" className="text-[10px]">{c.city}/{c.uf}</Badge>)}
                      </span>
                    </div>
                  )}
                  {(f.cep_ranges || []).length > 0 && (
                    <div className="flex items-start gap-1.5 text-xs text-slate-600">
                      <Hash className="h-3.5 w-3.5 mt-0.5 text-slate-400" />
                      <span>{f.cep_ranges.map((r) => `${r.start}–${r.end}`).join(", ")}</span>
                    </div>
                  )}
                  {f.lat != null && f.lng != null && (
                    <div className="flex items-center gap-1.5 text-xs text-emerald-700" data-testid={`franchisee-geo-${f.id}`}>
                      <MapPin className="h-3.5 w-3.5" />
                      <span>No mapa{f.base_cep ? ` · CEP ${f.base_cep}` : ""} ({Number(f.lat).toFixed(3)}, {Number(f.lng).toFixed(3)})</span>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <FranchiseeDialog franchisee={f} onSaved={reload}
                    trigger={<Button size="sm" variant="outline" className="flex-1 gap-1.5" data-testid={`edit-franchisee-${f.id}`}><Pencil className="h-4 w-4" /> Editar</Button>} />
                  <Button size="sm" variant="outline" className="flex-1 gap-1.5 text-amber-700 border-amber-200 hover:bg-amber-50"
                    onClick={() => resetPassword(f)} data-testid={`reset-franchisee-${f.id}`}>
                    <KeyRound className="h-4 w-4" /> Senha provisória
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <ResetPasswordDialog open={!!resetResult} onOpenChange={(o) => !o && setResetResult(null)} result={resetResult} />
    </div>
  );
}
